import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import GmailSettings from '../gmail/GmailSettings';
import { CardDavSettings } from './CardDavSettings';
import { BudgetShareSettings } from './BudgetShareSettings';
import styles from './IntegrationSettings.module.css';

type SettingsSection = 'gmail' | 'carddav' | 'budget-sharing' | null;

const getSectionFromPath = (pathname: string): SettingsSection => {
  if (pathname.endsWith('/gmail')) return 'gmail';
  if (pathname.endsWith('/carddav')) return 'carddav';
  if (pathname.endsWith('/budget-sharing')) return 'budget-sharing';
  return null;
};

/**
 * Consolidated settings screen for integrations.
 *
 * Renders Gmail integration and CardDAV settings in a single screen.
 *
 * Note: we keep /settings/gmail and /settings/carddav routes working by scrolling
 * to the relevant section on navigation.
 */
export const IntegrationSettings: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeSection = useMemo(() => getSectionFromPath(location.pathname), [location.pathname]);

  const scrollToSection = useCallback((section: Exclude<SettingsSection, null>) => {
    const el = document.getElementById(`settings-${section}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    if (!activeSection) return;
    // Defer to allow the DOM to paint first.
    const t = window.setTimeout(() => scrollToSection(activeSection), 0);
    return () => window.clearTimeout(t);
  }, [activeSection, scrollToSection]);

  return (
    <div className={styles.container}>
      <div className="mobile-page-title-anchor">
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.description}>
          Manage your integrations and sync settings.
        </p>
      </div>

      <div className={styles.sectionNav} aria-label="Settings sections">
        <Button
          variant={activeSection === 'gmail' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            navigate('/settings/gmail');
            scrollToSection('gmail');
          }}
        >
          Gmail
        </Button>
        <Button
          variant={activeSection === 'carddav' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            navigate('/settings/carddav');
            scrollToSection('carddav');
          }}
        >
          CardDAV
        </Button>
        <Button
          variant={activeSection === 'budget-sharing' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => {
            navigate('/settings/budget-sharing');
            scrollToSection('budget-sharing');
          }}
        >
          Budget Sharing
        </Button>
      </div>

      <section id="settings-gmail" className={styles.section} aria-label="Gmail integration settings">
        <h2 className={styles.sectionTitle}>Gmail Integration</h2>
        <p className={styles.sectionHint}>Connect accounts, pause monitoring, and disconnect</p>
        <GmailSettings embedded />
      </section>

      <section id="settings-carddav" className={styles.section} aria-label="CardDAV settings">
        <h2 className={styles.sectionTitle}>CardDAV</h2>
        <p className={styles.sectionHint}>Create app passwords and view connection info</p>
        <CardDavSettings embedded />
      </section>

      <section id="settings-budget-sharing" className={styles.section} aria-label="Budget sharing settings">
        <h2 className={styles.sectionTitle}>Budget Sharing</h2>
        <p className={styles.sectionHint}>Share your budget with others in read-only mode</p>
        <BudgetShareSettings embedded />
      </section>
    </div>
  );
};

export default IntegrationSettings;
